/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import { func, string } from 'prop-types';
import { Card } from 'ui-component-library';

import ActionButtons from './action-buttons';
import UpdateStatusAction from './actions/action-update-status';
import ResolveProblemAction from './actions/action-resolve-problem';
import BounceLoadAction from './actions/action-bounce-load';
import { CardContent, UnderConstructionContainer } from './action-card-tracking-actions.style';
import ProblemLogAction from './actions/action-log-problem';
import ManualTrackingAction from './actions/action-manual-tracking';
import loadShape from '../../../../shapes/load';
import {
  TOOLTIP_MESSAGES, MODE_TYPES, TL_TRACKING_ACTIONS, LTL_TRACKING_ACTIONS,
} from '../../../../constants';
import InfoTooltipButton from '../info-tooltip-button/info-tooltip-button';
import lockShape from '../../../../shapes/lock';

/**
 * Tracking Actions Card Component. If an event happens in this UI
 * (an action was submitted) refetchLoad is triggered.
 * @param {object} params - Object containing props for component
 * @param {object} params.load - Load object to perform action on
 * @param {func} params.refetchLoad - Function that handles refetching a given load
 * @param {object} params.lock - The lock (if present) on the load
 * @param {string} params.mode - Mode type
 * @return {element}
 */
const TrackingActionsCard = ({
  load, refetchLoad, lock, mode,
}) => {
  // State Hook
  const [selectedAction, setSelectedAction] = useState(null);

  const trackingActions = mode === MODE_TYPES.truckload ? TL_TRACKING_ACTIONS : LTL_TRACKING_ACTIONS;

  useEffect(() => {
    if (!lock?.ownedByUser) {
      setSelectedAction(null);
    }
  }, [lock]);

  /**
   * Handles when a user changes the selected action
   * @param {*} actionKey - Key of the new selected action
   * @return {void}
   */
  const handleActionChanged = (actionKey) => {
    setSelectedAction(selectedAction === trackingActions[actionKey] ? null : trackingActions[actionKey]);
  };

  /**
   * Renders a given TL action
   * @param {string} action - Action to render
   * @return {element} - View for appropriate action
   */
  const renderTLAction = (action) => {
    switch (action) {
      case trackingActions.updateStatus:
        return <UpdateStatusAction load={load} refetchLoad={refetchLoad} mode={mode} />;
      case trackingActions.logProblem:
        return <ProblemLogAction load={load} refetchLoad={refetchLoad} />;
      case trackingActions.resolveProblem:
        return <ResolveProblemAction load={load} refetchLoad={refetchLoad} />;
      case trackingActions.trackManualEvent:
        return <ManualTrackingAction load={load} refetchLoad={refetchLoad} />;
      case trackingActions.bounceLoad:
        return <BounceLoadAction load={load} />;
      default:
        return null;
    }
  };

  /**
   * Renders a given LTL action
   * @param {string} action - Action to render
   * @return {element} - View for appropriate action
   */
  const renderLTLAction = (action) => {
    switch (action) {
      case trackingActions.updateStatus:
        return <UpdateStatusAction load={load} refetchLoad={refetchLoad} mode={mode} />;
      case trackingActions.trackDriverLocation:
        return <ManualTrackingAction load={load} refetchLoad={refetchLoad} />;
      case trackingActions.addNote:
        return <UnderConstructionContainer>Under Construction</UnderConstructionContainer>;
      case trackingActions.logProblem:
        return <ProblemLogAction load={load} refetchLoad={refetchLoad} />;
      case trackingActions.resolveProblem:
        return <ResolveProblemAction load={load} refetchLoad={refetchLoad} />;
      case trackingActions.markForReconsignment:
        return <UnderConstructionContainer>Under Construction</UnderConstructionContainer>;
      case trackingActions.markForConsolidation:
        return <UnderConstructionContainer>Under Construction</UnderConstructionContainer>;
      case trackingActions.bounceLoad:
        return <BounceLoadAction load={load} />;

      default:
        return null;
    }
  };
  return (
    <Card
      title="Key Tracking Actions on This Load"
      headerContent={<InfoTooltipButton text={TOOLTIP_MESSAGES['load-actions']} isPrimary />}
      isPaddingRemoved
    >
      <ActionButtons
        lock={lock}
        selectedAction={selectedAction}
        actions={trackingActions}
        onActionChanged={handleActionChanged}
      />
      {selectedAction
        && (
          <CardContent>
            {mode === MODE_TYPES.truckload ? renderTLAction(selectedAction) : renderLTLAction(selectedAction)}
          </CardContent>
        )}
    </Card>
  );
};

TrackingActionsCard.propTypes = {
  load: loadShape.isRequired,
  refetchLoad: func,
  lock: lockShape,
  mode: string.isRequired,
};

TrackingActionsCard.defaultProps = {
  refetchLoad: () => { },
  lock: null,
};

export default TrackingActionsCard;
